import { MenuLinkItem } from '@affine/core/modules/app-sidebar/views';
import { DocDisplayMetaService } from '@affine/core/modules/doc-display-meta';
import {
  JOURNAL_SIDEBAR_TAB_ID,
  JournalService,
} from '@affine/core/modules/journal';
import { WorkbenchService } from '@affine/core/modules/workbench';
import { useI18n } from '@affine/i18n';
import { JournalIcon } from '@blocksuite/icons/rc';
import { useLiveData, useService } from '@toeverything/infra';
import type { ReactElement } from 'react';
import { useEffect, useState } from 'react';

import {
  MOBILE_NAVIGATION_RAIL_QUERY,
  useNavigationRailViewport,
} from './navigation-rail-viewport';

function useJournalDateAfterCommit(
  journalService: JournalService,
  docId: string
) {
  const [state, setState] = useState<{
    date: string | undefined;
    docId: string;
  }>({ date: undefined, docId: '' });

  useEffect(() => {
    let disposed = false;
    const subscription = journalService.journalDate$(docId).subscribe(date => {
      if (!disposed) {
        setState({ date, docId });
      }
    });

    return () => {
      disposed = true;
      subscription.unsubscribe();
    };
  }, [docId, journalService]);

  return state.docId === docId ? state.date : undefined;
}

function useDocIconAfterCommit(
  docDisplayMetaService: DocDisplayMetaService,
  docId: string
) {
  const [state, setState] = useState<{
    docId: string;
    Icon: typeof JournalIcon;
  }>({ docId: '', Icon: JournalIcon });

  useEffect(() => {
    let disposed = false;
    const subscription = docDisplayMetaService.icon$(docId).subscribe(Icon => {
      if (!disposed) {
        setState({ docId, Icon });
      }
    });

    return () => {
      disposed = true;
      subscription.unsubscribe();
    };
  }, [docDisplayMetaService, docId]);

  return state.docId === docId ? state.Icon : JournalIcon;
}

export const AppSidebarJournalButton = ({
  postfix,
}: {
  postfix?: ReactElement;
}) => {
  const t = useI18n();
  const docDisplayMetaService = useService(DocDisplayMetaService);
  const journalService = useService(JournalService);
  const workbench = useService(WorkbenchService).workbench;
  const sidebarOpen = useLiveData(workbench.sidebarOpen$);
  const mobileViewport = useNavigationRailViewport(
    MOBILE_NAVIGATION_RAIL_QUERY
  );
  const location = useLiveData(workbench.location$);
  const maybeDocId = location.pathname.split('/')[1];
  // Keep parametric LiveData factories out of React render here. These sources
  // activate upstream watchers when observed; subscribing after commit preserves
  // live updates without triggering cross-component render-phase updates.
  const isJournal = !!useJournalDateAfterCommit(journalService, maybeDocId);
  const JournalDisplayIcon = useDocIconAfterCommit(
    docDisplayMetaService,
    maybeDocId
  );
  const Icon = isJournal ? JournalDisplayIcon : JournalIcon;

  return (
    <MenuLinkItem
      data-testid="slider-bar-journals-button"
      active={isJournal || location.pathname.startsWith('/journals')}
      to={'/journals'}
      icon={<Icon />}
      postfix={postfix}
      onClick={() => {
        if (mobileViewport || sidebarOpen) return;
        workbench.activeView$.value.activeSidebarTab(JOURNAL_SIDEBAR_TAB_ID);
        workbench.openSidebar();
      }}
    >
      {t['com.streamlain.journal.app-sidebar-title']()}
    </MenuLinkItem>
  );
};
